/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { Box, Grid, Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import TabBofu from "@components/TabBofu/TabBofu";
import { InactivityHandler } from "@components/InactivityHandler";
import CustomRightImage from "@components/CustomRightImagev2";
import { CustomCompanyName } from "@components/CustomCompanyName";
import PersonFormTabs from "@components/PersonForm/PersonFormTabs";
import ButtonExpedienteStatus from "@components/ButtonExpedienteStatus";
import { useLazyGetPersonsQuery } from "@api/personasApi";
import { useLazyGetTabsConfigQuery } from "@api/catalogs";
import { useLazyGetDocumentAppIdQuery } from "@api/documents";
import DocumentsTabBofu from "@components/TabBofu/DocumentsTabBofu";
import { BofuTabDocument, PersonaStructure } from "@interfaces/response";
import {
  applyPersonDocumentChange,
  buildPersonDocuments,
  filterPersonsForFormHub,
  findPersonasTab,
  PersonDocumentChange,
} from "@components/PersonForm/personHelpers";
import { BOFU_TABS_CONFIG, BOFU_TABS_FORM_CODES } from "@helpers/constantes";
import { setParamsIdentityValidationForm } from "@store/slices/bofuSlice";
import { UUID } from "crypto";
import * as Sentry from "@sentry/react";
import { validateDocumentStructure } from "@components/TabBofu/functions";

const HUB_URL = BOFU_TABS_CONFIG.tab_personas.url;

const FormulariosPersona: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));

  const applicationData = useSelector((state: RootState) => state.register.application);
  const bofuLoading = useSelector((state: RootState) => state.bofu.isLoading);

  const [persons, setPersons] = useState<PersonaStructure[]>([]);
  const [documentsByPerson, setDocumentsByPerson] = useState<Record<string, BofuTabDocument[]>>({});
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const [isLoadingPersons, setIsLoadingPersons] = useState(true);
  const [hasPersonalForm, setHasPersonalForm] = useState(false);
  const [hasIdentityValidation, setHasIdentityValidation] = useState(false);

  const [getPersons] = useLazyGetPersonsQuery();
  const [getTabsConfig] = useLazyGetTabsConfigQuery();
  const [getDocumentsByAppId] = useLazyGetDocumentAppIdQuery();

  const selectedPerson = useMemo(
    () => persons.find((person) => person.id === selectedPersonId) || null,
    [persons, selectedPersonId],
  );

  const selectedDocuments = selectedPersonId ? documentsByPerson[selectedPersonId] || [] : [];

  const loadPersons = async () => {
    if (!applicationData?.id || !applicationData?.type) {
      setIsLoadingPersons(false);
      return;
    }

    try {
      setIsLoadingPersons(true);

      const [response, tabsConfig, uploadedDocuments] = await Promise.all([
        getPersons({ loanApplicationId: applicationData.id }).unwrap(),
        getTabsConfig({
          taxcode: applicationData.type,
          code: BOFU_TABS_CONFIG.tab_personas.code,
        }).unwrap(),
        getDocumentsByAppId({
          loanApplicationId: applicationData.id,
          status: true,
        }).unwrap(),
      ]);

      const personList = filterPersonsForFormHub(response || []);
      const personasTab = findPersonasTab(tabsConfig);
      const forms = personasTab?.forms || [];
      setHasPersonalForm(!!forms.find((f) => f.code === BOFU_TABS_FORM_CODES.formularios_persona));
      setHasIdentityValidation(!!forms.find((f) => f.code === BOFU_TABS_FORM_CODES.validacion_identidad));

      const tabDocuments = (personasTab?.documents || []).filter(validateDocumentStructure);
      const nextDocuments = Object.fromEntries(
        personList.map((person) => [
          person.id,
          buildPersonDocuments({
            person,
            tabDocuments,
            uploadedDocuments: uploadedDocuments || [],
            clientDetailsId: applicationData.client_details_id as UUID,
            allPersons: personList,
          }),
        ]),
      );

      setPersons(personList);
      setDocumentsByPerson(nextDocuments);
      setSelectedPersonId((current) => {
        if (current && personList.some((person) => person.id === current)) return current;
        return personList[0]?.id || null;
      });
    } catch (error) {
      Sentry.captureException(error, {
        extra: { component: "FormulariosPersona-loadPersons" },
      });
      setPersons([]);
      setDocumentsByPerson({});
      setSelectedPersonId(null);
    } finally {
      setIsLoadingPersons(false);
    }
  };

  useEffect(() => {
    loadPersons();
  }, [applicationData?.id, applicationData?.type]);

  const handleDocumentChange = (change: PersonDocumentChange) => {
    if (!selectedPersonId) return;

    setDocumentsByPerson((current) => ({
      ...current,
      [selectedPersonId]: applyPersonDocumentChange(current[selectedPersonId] || [], change),
    }));
  };

  const isLoading = bofuLoading || isLoadingPersons;

  const handleOpenForm = () => {
    if (!selectedPersonId) return;
    setTimeout(() => {
      navigate(`${HUB_URL}/${selectedPersonId}`);
    }, 200);
  };

  const handleClickIdentityValidation = () => {
    if (!selectedPersonId) return;
    dispatch(
      setParamsIdentityValidationForm({
        person_id: selectedPersonId,
        person_type: "PERSONA",
      }),
    );
    setTimeout(() => {
      navigate("/producto/creditosimple/validacion_identidad_persona");
    }, 200);
  };

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <Box textAlign="center" mx={4} pb={4}>
          <InactivityHandler />
          <CustomCompanyName />
          <TabBofu />

          {isLoading ? (
            <Box mt={3}>
              <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
            </Box>
          ) : (
            <Box>
              {persons.length === 0 ? (
                <Typography color="primary.dark" fontSize="20px" textAlign="center" fontWeight={600}>
                  No se encontraron personas registradas
                </Typography>
              ) : (
                <>
                  <Box mt={6}>
                    <PersonFormTabs
                      persons={persons}
                      selectedPersonId={selectedPersonId}
                      onSelectPerson={setSelectedPersonId}
                    />
                  </Box>

                  {selectedPerson && (
                    <Box mt={6}>
                      {(hasPersonalForm || hasIdentityValidation) && (
                        <Typography
                          mt={4}
                          variant={isMobileOrTablet ? "body2" : "body1"}
                          fontWeight={700}
                          color="primary.dark"
                        >
                          Ahora llena los formatos relacionados a la persona
                        </Typography>
                      )}

                      {hasPersonalForm && (
                        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body1" sx={{ flex: 1, textAlign: "left" }} color="primary.dark">
                            Información personal
                          </Typography>

                          <ButtonExpedienteStatus
                            mode="form"
                            formStatus={selectedPerson.personal_information_status}
                            onClick={handleOpenForm}
                          />
                        </Box>
                      )}

                      {hasIdentityValidation && (
                        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body1" sx={{ flex: 1, textAlign: "left" }} color="primary.dark">
                            Validación de identidad
                          </Typography>

                          <ButtonExpedienteStatus
                            mode="validation"
                            isCompleted={selectedPerson.identity_validation_completed}
                            onClick={handleClickIdentityValidation}
                          />
                        </Box>
                      )}

                      {selectedDocuments.length > 0 && (
                        <Box>
                          <Typography
                            mt={4}
                            variant={isMobileOrTablet ? "body2" : "body1"}
                            fontWeight={700}
                            color="primary.dark"
                          >
                            Ahora carga los documentos relacionados a la persona
                          </Typography>

                          <DocumentsTabBofu
                            key={selectedPerson.id}
                            inputPrefix={`persona_${selectedPerson.id}`}
                            tabCode={BOFU_TABS_CONFIG.tab_personas.code}
                            documents={selectedDocuments}
                            onChange={handleDocumentChange}
                            onChangeMultiple={handleDocumentChange}
                            RFC={selectedPerson.rfc}
                          />
                        </Box>
                      )}
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default FormulariosPersona;
